import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'

/** Which state the panel reports, which is the only thing that changes between the three. */
export type StateTone = 'neutral' | 'warning' | 'destructive'

/**
 * The mark above a state panel's heading.
 *
 * No outline and no solid disc: the fill is a tint of the panel's own foreground that fades out
 * towards the bottom, so the shape dissolves into the surface instead of sitting on it as a hard
 * circle. A tint of `foreground` rather than a surface token means it reads the same in both themes,
 * light on dark and dark on light, without naming a colour.
 */
const TONE: Record<StateTone, { fill: string; icon: string }> = {
	neutral: { fill: 'from-foreground/10', icon: 'text-foreground/80' },
	warning: { fill: 'from-warning/15', icon: 'text-warning' },
	destructive: { fill: 'from-destructive/15', icon: 'text-destructive' },
}

/**
 * Empty, error and access restricted share this so the three cannot drift apart again: a 14px icon
 * standing alone read as a stray glyph beside the 24px one the access panel already used.
 */
export function StateIcon({
	icon: Icon,
	tone = 'neutral',
	className,
}: { icon: LucideIcon; tone?: StateTone; className?: string }) {
	return (
		<span
			className={cn(
				'flex size-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-b to-transparent',
				TONE[tone].fill,
				className,
			)}
			aria-hidden='true'>
			<Icon className={cn('size-8', TONE[tone].icon)} />
		</span>
	)
}
